import debounce from 'lodash-es/debounce';
import { useEffect } from 'react';

import { smallMobileMaxWidth } from '../../constants/layout';
import { globalHeaderHeight } from '../GlobalHeader';
import type { UseCardStackScrollProps } from './types';

// How much of previous card should remain visible in px. defined in the TDD
// https://docs.google.com/document/d/1NKJFwdRNNvpkq61qy-lScmgVItlQTTkDH4X2IFK99u8/edit?pli=1&tab=t.0#heading=h.qfusgm7xt1b
const pickAmount = 40;

/** Hook to handle mobile card stacking steps. */
export const useCardStackScroll = ({
  childCount,
  cardRefs,
  containerRef,
  isEnabled,
}: UseCardStackScrollProps) => {
  useEffect(() => {
    if (!isEnabled || childCount <= 2) return;

    const container = containerRef.current;
    const cards = cardRefs.current
      .slice(0, childCount)
      .filter((card): card is HTMLElement => card !== null);

    if (!container || cards.length === 0) return;

    // Use requestAnimationFrame to ensure cards are rendered before measuring.
    const updateCardStyles = () => {
      // Check if we're on a small mobile screen (≤480px).
      const smallMobileMediaQuery = window.matchMedia(`(max-width: ${smallMobileMaxWidth}px)`);
      const isSmallMobile = smallMobileMediaQuery.matches;

      // Temporarily clear styles to measure natural card heights.
      for (const card of cards) {
        card.style.height = '';
        card.style.top = '';
        card.style.marginBottom = '';
        card.style.transform = '';
        card.style.transformOrigin = '';
      }

      // Measure all card heights and find the tallest.
      const cardHeights = cards.map(card => card.getBoundingClientRect().height);
      const tallestHeight = Math.max(...cardHeights);

      // Check if we need to scale down on small mobile screens.
      let scaleFactor = 1.0;

      if (isSmallMobile) {
        const viewportHeight = window.innerHeight;
        const availableHeight = viewportHeight - (globalHeaderHeight + pickAmount);

        if (tallestHeight > availableHeight) {
          scaleFactor = 0.8;
        }
      }

      // Set all cards to the tallest height and apply top positioning.
      // Cards must have all the same height for the animation to work.
      cards.forEach((card, index, array) => {
        card.style.height = `${tallestHeight}px`;
        card.style.top = `${globalHeaderHeight + pickAmount * (index + 1)}px`;
        const distanceFromEnd = array.length - index - 1;
        card.style.marginBottom = `${pickAmount * distanceFromEnd}px`;

        if (scaleFactor !== 1.0) {
          card.style.transform = `scale(${scaleFactor})`;
          card.style.transformOrigin = 'top';
        } else {
          card.style.transform = '';
          card.style.transformOrigin = '';
        }
      });
    };

    const handleResize = () => {
      requestAnimationFrame(updateCardStyles);
    };

    const debouncedAnimateScales = debounce(handleResize, 200, { trailing: true });
    window.addEventListener('resize', debouncedAnimateScales);

    requestAnimationFrame(updateCardStyles);

    return () => {
      window.removeEventListener('resize', debouncedAnimateScales);

      for (const card of cards) {
        card.style.height = '';
        card.style.top = '';
        card.style.marginBottom = '';
        card.style.transform = '';
        card.style.transformOrigin = '';
      }
    };
  }, [childCount, cardRefs, containerRef, isEnabled]);
};
